// ============================================================================
// vpn/VpnProfileForm.tsx — форма профиля VPN: ПОЛЯ ПО ПРОТОКОЛУ (SSoT клиента —
// protocolFields.ts), подсказки, кнопка «Проверить соединение» (сервер: VpnService
// TestVpnProfile — TCP / SSH-вход; UDP/QUIC — честное «проверка не выполняется»,
// без ложных «недоступен»), сохранение/удаление. Сохраняются ТОЛЬКО релевантные
// протоколу поля.
//
// Контракт результата проверки (VpnService.TestVpnProfile):
//   ok=true                — проверка пройдена (зелёный);
//   ok=false, error != ''  — проверка выполнена и НЕ прошла (красный);
//   ok=false, error == ''  — проверка НЕ ВЫПОЛНЯЕТСЯ для этого протокола
//                            (UDP/QUIC) — нейтральный вид + пояснение details.
// Для протоколов с probe='udp' кнопка не показывается — вместо неё честная
// подсказка (см. protocolFields.ts).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { PlugZap, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { VPN_INPUT_CLS, VPN_LABEL_CLS } from './uiClasses';
import { VPN_DEFAULT_PORTS, vpnDefaultPort, vpnProtoDef, type VpnFieldDef } from './protocolFields';

const log = createLogger('VpnProfileForm');

export interface VpnProfileFormData {
  id: string;
  code: string;
  name: string;
  synonym: string;
  protocol: string;
  server: string;
  port: number;
  publicKey: string;
  address: string;
  dns: string;
  allowedIps: string;
  isActive: boolean;
}

export interface VpnProtocolOption {
  code: string;
  label: string;
  kind: string; // store | extended
}

interface VpnTestResult {
  ok: boolean;
  error: string;
  latencyMs: number;
  details: string;
}

interface VpnProfileFormProps {
  form: VpnProfileFormData;
  setForm: React.Dispatch<React.SetStateAction<VpnProfileFormData>>;
  extra: Record<string, string>;
  extraInvalid: boolean;
  setExtraValue: (key: string, value: string) => void;
  psk: string;
  setPsk: (v: string) => void;
  privateKey: string;
  setPrivateKey: (v: string) => void;
  /** password — ssh-пароль (вход без ключей; пусто = не менять). */
  password: string;
  setPassword: (v: string) => void;
  protocols: VpnProtocolOption[];
  extendedEnabled: boolean;
  busy: boolean;
  onSave: () => void;
  onDelete: () => void;
}

export function VpnProfileForm({
  form, setForm, extra, extraInvalid, setExtraValue, psk, setPsk,
  privateKey, setPrivateKey, password, setPassword, protocols, extendedEnabled, busy, onSave, onDelete,
}: VpnProfileFormProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<VpnTestResult | null>(null);

  // Результат проверки относится к КОНКРЕТНОМУ профилю — при смене кода сбрасываем.
  useEffect(() => { setTestResult(null); }, [form.code]);

  const proto = vpnProtoDef(form.protocol);
  const available = protocols.filter((p) => p.kind === 'store' || extendedEnabled);
  const protoUnavailable = form.protocol !== '' && !available.some((p) => p.code === form.protocol);
  // UDP/QUIC-протоколы сервером НЕ проверяются (зеркало серверного реестра):
  // кнопка не показывается, вместо неё честная подсказка — не «недоступен».
  const udpOnly = proto.probe === 'udp';
  const testNeutral = testResult !== null && !testResult.ok && !testResult.error;

  const handleProtocolChange = useCallback((code: string) => {
    // Порт: подставляем порт по умолчанию НОВОГО протокола, если текущий пуст
    // или совпадает с портом по умолчанию любого протокола (пользователь его
    // не менял); кастомный порт сохраняется.
    setForm((f) => {
      const isDefaultPort = f.port === 0 || Object.values(VPN_DEFAULT_PORTS).includes(f.port);
      return { ...f, protocol: code, port: isDefaultPort ? vpnDefaultPort(code) : f.port };
    });
  }, [setForm]);

  const testConnection = async () => {
    if (!form.id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await getSDK().vpn.testVpnProfile({ profileCode: form.code });
      setTestResult({
        ok: !!r.ok, error: r.error, latencyMs: Number(r.latencyMs), details: r.details,
      });
    } catch (e) {
      log.error('test vpn profile', { error: e, code: form.code });
      setTestResult({
        ok: false,
        error: e instanceof Error ? e.message : 'Не удалось выполнить проверку',
        latencyMs: 0,
        details: '',
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">
        {form.id ? `Профиль: ${form.synonym || form.name || form.code}` : 'Новый профиль'}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1"><div className={VPN_LABEL_CLS}>Код</div>
          <input className={VPN_INPUT_CLS} value={form.code} disabled={!!form.id}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} /></div>
        <div className="space-y-1"><div className={VPN_LABEL_CLS}>Имя</div>
          <input className={VPN_INPUT_CLS} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
        <div className="space-y-1"><div className={VPN_LABEL_CLS}>Синоним</div>
          <input className={VPN_INPUT_CLS} value={form.synonym} onChange={(e) => setForm((f) => ({ ...f, synonym: e.target.value }))} /></div>
        <div className="space-y-1"><div className={VPN_LABEL_CLS}>Протокол</div>
          <select className={VPN_INPUT_CLS} value={form.protocol} disabled={protocols.length === 0}
            onChange={(e) => handleProtocolChange(e.target.value)}>
            {available.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
            {protoUnavailable && (
              <option value={form.protocol}>
                {protocols.find((p) => p.code === form.protocol)?.label || form.protocol} — недоступен (политика базы)
              </option>
            )}
            {protocols.length === 0 && <option value={form.protocol}>Загрузка протоколов…</option>}
          </select></div>
      </div>

      {proto.summary && (
        <div className="rounded-md border bg-muted/40 p-2 text-xs text-muted-foreground">{proto.summary}</div>
      )}

      {extraInvalid && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
          Дополнительные параметры профиля повреждены (не JSON) — при сохранении они будут
          заменены полями ниже.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {proto.fields.map((f) => (
          <ProtoField key={f.target + (f.extraKey || '')} def={f} form={form} setForm={setForm}
            extra={extra} setExtraValue={setExtraValue} psk={psk} setPsk={setPsk}
            privateKey={privateKey} setPrivateKey={setPrivateKey}
            password={password} setPassword={setPassword} />
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
        Профиль активен
      </label>

      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        <Button onClick={onSave} disabled={busy}>{form.id ? 'Сохранить' : 'Создать'}</Button>
        {form.id && !udpOnly && (
          <Button variant="outline" onClick={() => void testConnection()} disabled={busy || testing}>
            <PlugZap className={`h-3.5 w-3.5 mr-1 ${testing ? 'animate-pulse' : ''}`} />
            {testing ? 'Проверка…' : 'Проверить соединение'}
          </Button>
        )}
        {form.id && udpOnly && (
          <span className="text-xs text-muted-foreground">
            Проверка доступности для этого протокола не выполняется (UDP/QUIC).
          </span>
        )}
        {form.id && (
          <Button variant="ghost" size="sm" className="text-destructive ml-auto" onClick={onDelete} disabled={busy}>
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Удалить
          </Button>
        )}
      </div>

      {testResult && (
        <div className={`rounded-md border p-2 text-xs space-y-0.5 ${
          testResult.ok
            ? 'border-green-600/40 bg-green-600/5'
            : testNeutral
              ? 'border-border bg-muted/40'
              : 'border-destructive/40 bg-destructive/5'}`}>
          <div className={
            testResult.ok ? 'text-green-600'
              : testNeutral ? 'text-muted-foreground font-medium'
                : 'text-destructive'}>
            {testResult.ok
              ? `✓ Сервер доступен · ${testResult.latencyMs} мс`
              : testNeutral
                ? 'Проверка доступности для этого протокола не выполняется (UDP/QUIC)'
                : `✕ ${testResult.error || 'проверка не пройдена'}`}
          </div>
          {testResult.details && <div className="text-muted-foreground">{testResult.details}</div>}
        </div>
      )}
    </div>
  );
}

// ----- поле протокола (единая точка рендера ВСЕХ целей) -----

interface ProtoFieldProps {
  def: VpnFieldDef;
  form: VpnProfileFormData;
  setForm: React.Dispatch<React.SetStateAction<VpnProfileFormData>>;
  extra: Record<string, string>;
  setExtraValue: (key: string, value: string) => void;
  psk: string;
  setPsk: (v: string) => void;
  privateKey: string;
  setPrivateKey: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
}

function ProtoField({ def, form, setForm, extra, setExtraValue, psk, setPsk, privateKey, setPrivateKey, password, setPassword }: ProtoFieldProps) {
  const value = fieldValue(def, form, extra, psk, privateKey, password);
  const onChange = (v: string) => {
    switch (def.target) {
      case 'server': setForm((f) => ({ ...f, server: v })); break;
      case 'port': setForm((f) => ({ ...f, port: Math.round(Number(v) || 0) })); break;
      case 'publicKey': setForm((f) => ({ ...f, publicKey: v })); break;
      case 'address': setForm((f) => ({ ...f, address: v })); break;
      case 'dns': setForm((f) => ({ ...f, dns: v })); break;
      case 'allowedIps': setForm((f) => ({ ...f, allowedIps: v })); break;
      case 'psk': setPsk(v); break;
      case 'privateKey': setPrivateKey(v); break;
      case 'password': setPassword(v); break;
      case 'extra': setExtraValue(def.extraKey || '', v); break;
    }
  };
  const secret = def.target === 'psk' || def.target === 'privateKey' || def.target === 'password'
    || def.type === 'password';
  const isWide = def.type === 'textarea' || def.target === 'server' || def.target === 'publicKey'
    || def.target === 'address';
  // Секреты не возвращаются сервером: пустое поле = не менять сохранённое.
  const secretEmpty = (def.target === 'psk' && psk === '')
    || (def.target === 'privateKey' && privateKey === '')
    || (def.target === 'password' && password === '');
  const label = secretEmpty ? `${def.label} (пусто = не менять)` : def.label;

  return (
    <div className={`space-y-1 ${isWide ? 'md:col-span-2' : ''}`}>
      <div className={VPN_LABEL_CLS}>{label}</div>
      {def.type === 'select' ? (
        <select className={VPN_INPUT_CLS} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— не задано —</option>
          {(def.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : def.type === 'textarea' ? (
        <textarea className={`${VPN_INPUT_CLS} h-20 py-1`} value={value}
          placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={VPN_INPUT_CLS} type={secret ? 'password' : def.type === 'number' ? 'number' : 'text'}
          value={value} placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
      {def.hint && <div className="text-[11px] text-muted-foreground">{def.hint}</div>}
    </div>
  );
}

/** Текущее значение поля (единый резолв цель → значение). */
function fieldValue(def: VpnFieldDef, form: VpnProfileFormData, extra: Record<string, string>, psk: string, privateKey: string, password: string): string {
  switch (def.target) {
    case 'server': return form.server;
    case 'port': return form.port ? String(form.port) : '';
    case 'publicKey': return form.publicKey;
    case 'address': return form.address;
    case 'dns': return form.dns;
    case 'allowedIps': return form.allowedIps;
    case 'psk': return psk;
    case 'privateKey': return privateKey;
    case 'password': return password;
    case 'extra': return extra[def.extraKey || ''] || '';
  }
}
